import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { displaySuccessMessage, displayErrorMessage } from 'redux/notificationBar/notificationBarSlice';
import { deleteAttachment } from 'api/activityReport';
import { getFileDownloadUrl } from './patrolUtils';
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';

const useFileActions = ({
  onRefetch,
  customerName,
  locationName,
  selectedPatrol,
  setSelectedPatrol,
  setExpandedPatrolsData,
  expandedPatrolIds,
  setExpandedPatrolIds,
}) => {
  const dispatch = useDispatch();

  const [viewingFile, setViewingFile] = useState(null);
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);

  const [fileToDelete, setFileToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadPatrolId, setUploadPatrolId] = useState(null);
  const [uppy, setUppy] = useState(null);

  const handleViewFile = (file) => {
    setViewingFile(file);
    setIsFileViewerOpen(true);
  };

  const handleCloseFileViewer = () => {
    setIsFileViewerOpen(false);
    setViewingFile(null);
  };

  const handleDownloadFile = (file) => {
    const url = getFileDownloadUrl(file);
    window.open(url, '_blank');
  };

  const handleDeleteFileClick = (file) => {
    setFileToDelete(file);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDeleteFile = async () => {
    if (!fileToDelete) return;

    setIsDeletingFile(true);
    try {
      await deleteAttachment(fileToDelete.id, fileToDelete.type || 'vv');

      if (selectedPatrol) {
        setSelectedPatrol((prev) => ({
          ...prev,
          attachments: prev.attachments?.filter((f) => f.id !== fileToDelete.id) || [],
        }));
      }

      setExpandedPatrolsData((prev) => {
        const updated = { ...prev };
        for (const patrolId in updated) {
          if (updated[patrolId]?.attachments) {
            updated[patrolId] = {
              ...updated[patrolId],
              attachments: updated[patrolId].attachments.filter((f) => f.id !== fileToDelete.id),
            };
          }
        }
        return updated;
      });

      setIsDeleteDialogOpen(false);
      setFileToDelete(null);
      if (onRefetch) onRefetch();
    } catch (error) {
      console.error('Failed to delete file:', error);
      dispatch(displayErrorMessage('Failed to delete file. Please try again.'));
    } finally {
      setIsDeletingFile(false);
    }
  };

  const getUniqueId = async () => {
    const response = await fetch('/api/fileuploadservice/getUniqueId', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      body: JSON.stringify({}),
    });
    return response.json();
  };

  const handleOpenUploadDialog = (patrol) => {
    const patrolId = patrol.id;
    setUploadPatrolId(patrolId);

    const patrolDate = patrol.startTime
      ? new Date(patrol.startTime.replace(' ', 'T')).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '/')
      : '';
    const startTimeISO = patrol.startTime ? patrol.startTime : '';

    const uploadMeta = {
      customerName: customerName || '',
      addressName: locationName || '',
      patrolId,
      patrolDate,
      startTime: startTimeISO,
    };

    const uppyInstance = new Uppy({
      meta: uploadMeta,
      restrictions: {
        allowedFileTypes: ['image/*', 'video/*', 'application/pdf'],
      },
      autoProceed: false,
    })
      .use(XHRUpload, {
        endpoint: '/api2.0/candds/upload',
        formData: true,
        fieldName: 'Files',
        timeout: 0,
      })
      .on('file-added', async (file) => {
        const contentType = file.type?.split('/')[0];
        if (contentType === 'image') {
          try {
            const mucResponse = await getUniqueId();
            uppyInstance.setFileState(file.id, {
              xhrUpload: {
                ...file.xhrUpload,
                endpoint: `/Project/Webservice/FileUpload.aspx/UploadFile?muc=${mucResponse.d}&cml=${patrolId}&wbs=undefined&is=n`,
                fieldName: 'filedata',
              },
            });
          } catch (err) {
            console.error('Failed to get unique ID for upload:', err);
          }
        }
      })
      .on('upload-success', () => {
        dispatch(displaySuccessMessage('File uploaded successfully'));
      })
      .on('complete', (result) => {
        if (result.successful.length > 0) {
          if (onRefetch) onRefetch();
          if (expandedPatrolIds.has(patrolId)) {
            setExpandedPatrolIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(patrolId);
              return newSet;
            });
          }
        }
      })
      .on('error', (error) => {
        console.error('Upload error:', error);
        dispatch(displayErrorMessage('Failed to upload file. Please try again.'));
      });

    setUppy(uppyInstance);
    setIsUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setIsUploadDialogOpen(false);
    if (uppy) {
      uppy.close();
      setUppy(null);
    }
    setUploadPatrolId(null);
  };

  return {
    viewingFile,
    isFileViewerOpen,
    handleViewFile,
    handleCloseFileViewer,
    handleDownloadFile,
    fileToDelete,
    isDeleteDialogOpen,
    isDeletingFile,
    handleDeleteFileClick,
    handleConfirmDeleteFile,
    setIsDeleteDialogOpen,
    setFileToDelete,
    isUploadDialogOpen,
    uppy,
    handleOpenUploadDialog,
    handleCloseUploadDialog,
  };
};

export default useFileActions;
